import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import type { CampaignOwner, Displays_AudienceVersion } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { useAPIClientRequest } from 'c-hooks';
import ApiClient from 'c-data/apiClient';
import { Alert } from 'c-components';
import {
    buildCampaignOwnerAudiencePayload,
    initialOwnerAudienceRow,
    LAST_PLAYED_FALLBACK,
    valueForAudienceType,
} from './audienceLib';

const Audience = ({
    data,
    loading,
    listData,
    campError,
    audienceListError,
    campStart,
    listStart,
    isLastPlayedEditable,
}: {
    data: CampaignOwner[];
    loading: boolean;
    listData: Displays_AudienceVersion[];
    campError: string | null;
    audienceListError: string | null;
    campStart: () => void;
    listStart: () => void;
    isLastPlayedEditable: boolean;
}) => {
    const [formState, setFormState] = useState<{ [key: number]: any }>({});
    const t = useCommonTranslation();

    const audienceTypeOptions = [
        'default',
        'logs',
        'audience',
        t('Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.impressions_per_play'),
    ];

    useEffect(() => {
        if (!loading && data.length > 0) {
            const initialState: { [key: number]: any } = {};

            data.forEach(owner => {
                initialState[owner.owner_id] = initialOwnerAudienceRow(
                    owner,
                    t(
                        'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.impressions_per_play',
                    ),
                );
            });

            setFormState(initialState);
        }
    }, [data, loading, t]);

    const [saveStatus, setSaveStatus] = useState<{ [key: number]: 'idle' | 'saving' | 'saved' }>(
        {},
    );

    const handleAudienceTypeChange = (ownerId: number, newType: string) => {
        const translatedImpressionType = t(
            'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.impressions_per_play',
        );

        const apiOwner = data.find(o => o.owner_id === ownerId);
        const newValue = valueForAudienceType(apiOwner, newType, translatedImpressionType);

        setFormState(prev => ({
            ...prev,
            [ownerId]: { ...prev[ownerId], audience_type: newType, value: newValue },
        }));
    };

    const handleSecondaryChange = (ownerId: number, value: any) => {
        setFormState(prev => ({
            ...prev,
            [ownerId]: { ...prev[ownerId], value },
        }));
    };

    const handleLastPlayedChange = (ownerId: number, lastPlayed: 'log' | 'media') => {
        setFormState(prev => ({
            ...prev,
            [ownerId]: { ...prev[ownerId], last_played_date_by: lastPlayed },
        }));
    };

    const { start, error, reset } = useAPIClientRequest(ApiClient.Entities.Schedule.campaignOwner);

    const handleSave = async (
        campaignOwnerId: number,
        audience_type: string,
        value?: string | number,
        lastPlayed?: 'log' | 'media',
    ) => {
        const translatedImpressionType = t(
            'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.impressions_per_play',
        );

        const payload = buildCampaignOwnerAudiencePayload(
            audience_type,
            value,
            lastPlayed,
            translatedImpressionType,
        );

        setSaveStatus(prev => ({ ...prev, [campaignOwnerId]: 'saving' }));

        try {
            await start(campaignOwnerId, payload);
            setSaveStatus(prev => ({ ...prev, [campaignOwnerId]: 'saved' }));

            setTimeout(() => {
                setSaveStatus(prev => ({ ...prev, [campaignOwnerId]: 'idle' }));
            }, 3000);
        } catch {
            setSaveStatus(prev => ({ ...prev, [campaignOwnerId]: 'idle' }));
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box mt={2} width="100%">
            {campError && (
                <Alert severity="error" onClose={campStart}>
                    {String(campError)}
                </Alert>
            )}

            {audienceListError && (
                <Alert severity="error" onClose={listStart}>
                    {String(audienceListError)}
                </Alert>
            )}

            {error && (
                <Alert severity="error" onClose={reset}>
                    {String(error)}
                </Alert>
            )}

            <Table sx={{ width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <strong>
                                {t(
                                    'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.owner',
                                )}
                            </strong>
                        </TableCell>
                        <TableCell>
                            <strong>
                                {t(
                                    'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.lastPlayed',
                                )}
                            </strong>
                        </TableCell>
                        <TableCell>
                            <strong>
                                {t(
                                    'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.audienceType',
                                )}
                            </strong>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {data.map(owner => {
                        const currentType =
                            formState[owner.owner_id]?.audience_type ||
                            owner.audience_type ||
                            'default';

                        const secondaryValue =
                            formState[owner.owner_id]?.value ??
                            (owner.audience_type === 'impressions_per_play'
                                ? Number(owner.impressions_per_play)
                                : owner.audience_version_id ?? '');

                        const lastPlayed =
                            formState[owner.owner_id]?.last_played_date_by ?? LAST_PLAYED_FALLBACK;

                        return (
                            <TableRow key={owner.owner_id}>
                                <TableCell>{owner?.owner?.name ?? ''}</TableCell>

                                <TableCell>
                                    <Select
                                        value={lastPlayed}
                                        onChange={e =>
                                            handleLastPlayedChange(
                                                owner.owner_id,
                                                e.target.value as 'log' | 'media',
                                            )
                                        }
                                        size="small"
                                        disabled={!isLastPlayedEditable}
                                        sx={{ minWidth: 120 }}
                                    >
                                        <MenuItem value="default">
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.lastPlayedOptions.default',
                                            )}
                                        </MenuItem>
                                        <MenuItem value="media">
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.lastPlayedOptions.media',
                                            )}
                                        </MenuItem>
                                    </Select>
                                </TableCell>

                                <TableCell>
                                    <Box display="flex" gap={1} alignItems="center">
                                        <Select
                                            value={currentType}
                                            onChange={e =>
                                                handleAudienceTypeChange(
                                                    owner.owner_id,
                                                    e.target.value,
                                                )
                                            }
                                            size="small"
                                        >
                                            {audienceTypeOptions.map(opt => (
                                                <MenuItem key={opt} value={opt}>
                                                    {opt}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {currentType === 'audience' && (
                                            <Select
                                                value={secondaryValue}
                                                onChange={e =>
                                                    handleSecondaryChange(
                                                        owner.owner_id,
                                                        e.target.value,
                                                    )
                                                }
                                                size="small"
                                                displayEmpty
                                                sx={{ minWidth: 220 }}
                                            >
                                                <MenuItem value="">
                                                    <em>
                                                        {t(
                                                            'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.selectVersion',
                                                        )}
                                                    </em>
                                                </MenuItem>
                                                {listData.map(version => (
                                                    <MenuItem key={version.id} value={version.id}>
                                                        {version.version_name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}

                                        {currentType ===
                                            t(
                                                'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.impressions_per_play',
                                            ) && (
                                            <TextField
                                                size="small"
                                                placeholder={t(
                                                    'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.impressions_per_play',
                                                )}
                                                type="number"
                                                inputProps={{ min: 0 }}
                                                value={secondaryValue}
                                                onChange={e =>
                                                    handleSecondaryChange(
                                                        owner.owner_id,
                                                        e.target.value,
                                                    )
                                                }
                                                sx={{ maxWidth: 180 }}
                                            />
                                        )}

                                        <Button
                                            onClick={() =>
                                                handleSave(
                                                    owner.id,
                                                    currentType,
                                                    secondaryValue,
                                                    lastPlayed,
                                                )
                                            }
                                            variant="outlined"
                                            size="small"
                                        >
                                            {t(
                                                'Modules.Main.Campaigns.CampaignSchedule.scheduleTabs.audienceTab.save',
                                            )}
                                        </Button>

                                        {saveStatus[owner.id] === 'saving' && (
                                            <CircularProgress size={18} />
                                        )}

                                        {saveStatus[owner.id] === 'saved' && (
                                            <Box color="success.main" fontSize={20} lineHeight={1}>
                                                ✓
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
};

export default Audience;
